import { Dispatch, FC, SetStateAction, useState } from "react";
import { Alert, Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading, FormCheck } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { formatDateToString } from "go-core/components/Formatters/FormatDate";
import { FormDatePicker } from "go-form/components/FormDatePicker";
import { FormDirty } from "go-form/components/FormDirty";
import { apiOrganization } from "../../../../../../../../services/Api/Organization/apiOrganization";
import { InvoiceApi, KSeFInvoiceApiStatus } from "../../../../../../../../services/Api/Organization/types";
import { useCreateOfflineInvoice } from "../../hooks/useCreateOfflineInvoice";
import { useKSeFStatusRequests } from "../../hooks/useKSeFStatusRequests";
import { exportInvoicePdf, printInvoicePdf } from "../../services/exportInvoice";
import { ExportInvoiceModalMode } from "../../utils";
import { InvoicePreviewKSeFAlert } from "../Preview/KSeF/InvoicePreviewKSeFAlert";

interface Props {
	mode: ExportInvoiceModalMode;
	onHide: () => void;
	invoice: InvoiceApi;
	refetchInvoice: () => Promise<InvoiceApi | null>;
	setInvoice: Dispatch<SetStateAction<InvoiceApi | undefined>>;
}

interface FormProps {
	duplicate: boolean;
	duplicate_date: string;
	send_to_ksef: boolean;
}

const ExportInvoicePdfModal: FC<Props> = ({ mode, onHide, invoice, refetchInvoice, setInvoice }) => {
	const { t } = useTranslation();
	const [loading, setLoading] = useState<boolean>(false);
	const { addFlash } = useFlash();
	const sendToKSeFAutomatically = invoice.settings.is_send_to_ksef_automatically;
	const displaySendToKSeFCheckbox =
		!sendToKSeFAutomatically &&
		(!invoice.ksef_data ||
			(Boolean(invoice.ksef_data) && invoice.ksef_data.status !== KSeFInvoiceApiStatus.SUCCESS));
	const form = useForm<FormProps>({
		criteriaMode: "all",
		defaultValues: { duplicate: false, duplicate_date: "", send_to_ksef: sendToKSeFAutomatically },
	});
	const {
		formState,
		handleSubmit,
		formState: { errors },
		control,
		setError,
		watch,
		setValue,
		register,
	} = form;
	const displayMissingInvoiceInKSeFSystem =
		!invoice.ksef_data ||
		(invoice.ksef_data.status !== KSeFInvoiceApiStatus.SUCCESS &&
			(invoice.ksef_data.status !== KSeFInvoiceApiStatus.PENDING || !invoice.ksef_data.online));
	const watchedDuplicate = watch("duplicate");
	const fetchInvoice = async () => {
		return apiOrganization.getInvoice(invoice.id, { include: "ksef" });
	};
	const {
		manageInvoicePreviewRefetching,
		refetchingInvoiceInProgress,
		sendToKSeFStatus,
		displayKSeFUninstalledAlert,
	} = useKSeFStatusRequests(
		fetchInvoice,
		invoice.id,
		invoice.settings.ksef_token_exists || invoice?.settings.ksef_auth_data_exists
	);
	useKSeFStatusRequests(fetchInvoice, invoice.id, invoice.settings.ksef_token_exists);
	const { KSeFServiceUnavailable, createOfflineInvoiceHandler, offlineCreationLoading, displayNoOfflineCertAlert } =
		useCreateOfflineInvoice(invoice, setInvoice, () => onHide());

	const onSubmit = handleSubmit(async ({ duplicate_date, send_to_ksef }) => {
		try {
			if (send_to_ksef) {
				await manageInvoicePreviewRefetching();
				if (sendToKSeFStatus.current !== KSeFInvoiceApiStatus.SUCCESS) {
					const addInvoiceToKSeFRequestSent = sendToKSeFStatus.current !== undefined;

					if (addInvoiceToKSeFRequestSent) {
						await refetchInvoice();
						addFlash({
							type: "danger",
							msg: t("modules.invoice.action.error_on_adding_invoice_to_ksef_occurred.title"),
							messageAlreadyCreated: true,
						});
					}
					return;
				}
			}
			setLoading(true);
			const params: Record<string, any> = { duplicate_date };
			if (mode === ExportInvoiceModalMode.EXPORT_TO_PDF) {
				await exportInvoicePdf(invoice, t, params);
			} else {
				await printInvoicePdf(invoice);
			}
			await refetchInvoice();
			onHide();
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	const toggleDuplicate = () => {
		if (!watchedDuplicate) {
			setValue("duplicate_date", formatDateToString(new Date()));
		} else {
			setValue("duplicate_date", "");
		}
	};

	return (
		<Modal onHide={onHide} show={Boolean(mode)}>
			<FormDirty formState={formState} key="export-invoice-pdf-modal-form" onSubmit={onSubmit}>
				<Modal.Header closeButton>
					<Modal.Title>
						{t(
							mode === ExportInvoiceModalMode.EXPORT_TO_PDF
								? "modules.invoice.action.export_invoice_pdf_modal.title"
								: "lib:common.action.print"
						)}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{invoice.status !== "CONFIRMED" && (
						<Alert variant="warning">
							{mode === ExportInvoiceModalMode.EXPORT_TO_PDF
								? t("modules.invoice.field.confirm_export_invoice_to_pdf.title")
								: t("modules.invoice.field.confirm_print_invoice.title")}
						</Alert>
					)}
					{displayMissingInvoiceInKSeFSystem && !KSeFServiceUnavailable && (
						<Alert variant="warning">
							{t("modules.invoice.field.missing_invoice_in_KSeF_system.title")}
						</Alert>
					)}
					{KSeFServiceUnavailable && (
						<Alert variant="warning">{t("modules.invoice.field.KSeF_service_unavailable.title")}</Alert>
					)}
					<InvoicePreviewKSeFAlert
						displayKSeFUninstalledAlert={displayKSeFUninstalledAlert}
						displayNoOfflineCertAlert={displayNoOfflineCertAlert}
					/>
					<span>{t("modules.invoice.field.export_invoice_pdf_modal_duplicate.help_text.description")}</span>
					<div className="mt-2">
						<FormCheck
							type="switch"
							name="duplicate"
							label={t("modules.invoice.field.export_invoice_pdf_modal_duplicate.title")}
							onClick={toggleDuplicate}
							register={register}
							errors={errors}
						/>
						{displaySendToKSeFCheckbox && (
							<FormCheck
								name="send_to_ksef"
								errors={errors}
								register={register}
								type="switch"
								label={t("modules.invoice.action.send_to_ksef.title")}
							/>
						)}
						{watchedDuplicate && (
							<FormDatePicker
								errors={errors}
								timePicker={false}
								label={t("modules.invoice.field.export_invoice_pdf_modal_duplicate_date.title")}
								control={control}
								name="duplicate_date"
							/>
						)}
					</div>
				</Modal.Body>
				<Modal.Footer>
					{KSeFServiceUnavailable && (
						<ButtonLoading loading={offlineCreationLoading} onClick={createOfflineInvoiceHandler}>
							{t("modules.invoice.action.create_invoice_in_offline_mode.title")}
						</ButtonLoading>
					)}
					<ButtonLoading loading={loading || refetchingInvoiceInProgress} type="submit" onClick={onSubmit}>
						{t(
							mode === ExportInvoiceModalMode.EXPORT_TO_PDF
								? "common.action.export_pdf"
								: "common.action.print",
							{ ns: "lib" }
						)}
					</ButtonLoading>
					<Button variant="light" onClick={onHide}>
						{t("common.action.cancel", { ns: "lib" })}
					</Button>
				</Modal.Footer>
			</FormDirty>
		</Modal>
	);
};

export default ExportInvoicePdfModal;
